import React, { useEffect, useState } from 'react';
import DocumentActionButtons from './DocumentActionButtons';
import './DocumentPreviewTable.css';

const fileKind = document => {
  const sources = [document.fileName, document.url].map(value => String(value || ''));
  if (sources.some(source => /\.(jpe?g|png|gif|webp)(?:$|[?#])/i.test(source))) return 'image';
  if (sources.some(source => /\.pdf(?:$|[?#])/i.test(source))) return 'pdf';
  return 'file';
};

export default function DocumentPreviewTable({ documents }) {
  const [authenticatedUrls, setAuthenticatedUrls] = useState({});
  const [previewFailures, setPreviewFailures] = useState({});
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const objectUrls = [];
    const token = localStorage.getItem('token');
    const requiresAuthentication = url => /\/api\/tenant\/[^/]+\/certificates\/\d+\/documents\/[a-f0-9]{24}(?:$|[?#])/i.test(String(url || ''));
    const protectedImages = (documents || []).filter(document => (
      requiresAuthentication(document.url) && fileKind(document) === 'image'
    )).slice(0, 12);
    if (!protectedImages.length) {
      return () => controller.abort();
    }
    const load = async () => {
      const entries = [];
      for (const document of protectedImages) {
        const index = (documents || []).indexOf(document);
        try {
          const response = await fetch(document.url, {
            signal: controller.signal,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          if (!response.ok) {
            setPreviewFailures(current => ({ ...current, [`${document.key || index}`]: true }));
            continue;
          }
          const objectUrl = URL.createObjectURL(await response.blob());
          objectUrls.push(objectUrl);
          entries.push([`${document.key || index}`, objectUrl]);
        } catch (error) {
          if (!controller.signal.aborted) setPreviewFailures(current => ({ ...current, [`${document.key || index}`]: true }));
        }
      }
      if (!controller.signal.aborted) setAuthenticatedUrls(Object.fromEntries(entries));
    };
    load();
    return () => {
      controller.abort();
      objectUrls.forEach(url => URL.revokeObjectURL?.(url));
    };
  }, [documents]);

  return (
    <div className="document-preview-table-wrap">
      {actionError && <p role="alert" className="document-preview-error">{actionError}</p>}
      <table className="document-preview-table">
        <thead><tr><th>कागदपत्र</th><th>पूर्वावलोकन</th><th>कृती</th></tr></thead>
        <tbody>
          {documents.map((document, index) => {
            const authenticatedUrl = authenticatedUrls[`${document.key || index}`];
            const requiresAuthentication = /\/api\/tenant\/[^/]+\/certificates\/\d+\/documents\/[a-f0-9]{24}(?:$|[?#])/i.test(String(document.url || ''));
            const displayUrl = authenticatedUrl || document.url;
            const kind = fileKind({ ...document, url: displayUrl });
            const fileName = document.fileName || document.name || `document-${index + 1}`;
            return (
              <tr key={document.key || `${fileName}-${index}`}>
                <td data-label="कागदपत्र" className="document-preview-name">
                  <strong>{document.name || `कागदपत्र ${index + 1}`}</strong>
                  <small>{fileName}</small>
                </td>
                <td data-label="पूर्वावलोकन">
                  <div className="document-preview-thumb">
                    {kind === 'image' && authenticatedUrl && <img src={authenticatedUrl} alt={`${document.name || fileName} preview`} />}
                    {kind === 'image' && requiresAuthentication && !authenticatedUrl && <span aria-label={previewFailures[`${document.key || index}`] ? 'Preview unavailable' : 'Preview loading'}>🖼️</span>}
                    {kind === 'image' && !requiresAuthentication && <img src={displayUrl} alt={`${document.name || fileName} preview`} />}
                    {(kind === 'pdf' || kind === 'file') && <span aria-hidden="true">📄</span>}
                  </div>
                </td>
                <td data-label="कृती" className="document-preview-actions">
                  <DocumentActionButtons
                    url={requiresAuthentication ? document.url : displayUrl}
                    previewUrl={kind === 'image' ? authenticatedUrl || displayUrl : null}
                    fileName={fileName}
                    allowPreview={kind === 'image' && (!requiresAuthentication || Boolean(authenticatedUrl))}
                    onDownloadError={() => setActionError(`${fileName} डाउनलोड करता आले नाही.`)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
